import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdAdd, MdDelete, MdShoppingCart, MdWorkspaces, MdWarning } from 'react-icons/md';
import { pedidosAPI, inventarioAPI, clientesAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import { useConfig } from '../context/ConfigContext';

/* ─── Styled ─── */
const Container = styled.div`width: 100%; max-width: 100%;`;

const PageHeader = styled.div`
  display: flex; align-items: center; gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl}; flex-wrap: wrap;
`;

const BackButton = styled.button`
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary}; cursor: pointer;
  svg { font-size: 1.5rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl}; color: ${p => p.theme.colors.textPrimary}; margin: 0;
  display: flex; align-items: center; gap: 10px;
  svg { color: ${p => p.theme.colors.primary}; }
`;

const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const CardTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg}; color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0; font-weight: 700;
  padding-bottom: ${p => p.theme.spacing.md};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${p => p.theme.spacing.lg};
`;

const FieldGroup = styled.div`display: flex; flex-direction: column; gap: ${p => p.theme.spacing.sm};`;
const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary}; font-family: ${p => p.theme.fonts.primary};
  min-height: 80px; resize: vertical;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

/* Items cards */
const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 40px;
  gap: ${p => p.theme.spacing.md};
  align-items: end;
  margin-bottom: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ItemFieldGroup = styled.div`
  display: flex; flex-direction: column; gap: ${p => p.theme.spacing.xs};
`;

const ItemLabel = styled.label`
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600; color: ${p => p.theme.colors.textSecondary};
`;

const ItemInput = styled.input`
  width: 100%; padding: 8px ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &[readonly] { background: ${p => p.theme.colors.background}; cursor: not-allowed; }
`;

const ItemSelect = styled.select`
  width: 100%; padding: 8px ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const DelBtn = styled.button`
  width: 28px; height: 28px; border: none; border-radius: 6px;
  background: ${p => p.theme.colors.danger}10; color: ${p => p.theme.colors.danger};
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  svg { font-size: 1rem; }
  &:hover { background: ${p => p.theme.colors.danger}20; }
`;

const AddItemBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 8px 14px; margin-top: ${p => p.theme.spacing.md};
  border: 1.5px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: none; color: ${p => p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm}; font-family: ${p => p.theme.fonts.primary};
  cursor: pointer; transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.1rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const TotalRow = styled.div`
  display: flex; justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.lg};
  font-size: ${p => p.theme.fontSizes.xl}; font-weight: 800;
  color: ${p => p.theme.colors.primary};
`;

const ButtonRow = styled.div`
  display: flex; gap: ${p => p.theme.spacing.md}; justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.xl};
`;

const Btn = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background: ${p => p.primary
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex; align-items: center; gap: 6px;
  cursor: pointer; transition: ${p => p.theme.transitions.fast};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

const ErrMsg = styled.div`
  color: ${p => p.theme.colors.danger}; font-size: ${p => p.theme.fontSizes.sm};
  padding: ${p => p.theme.spacing.md}; background: ${p => p.theme.colors.danger}10;
  border-radius: ${p => p.theme.borderRadius.md}; border-left: 3px solid ${p => p.theme.colors.danger};
  margin: 0;
`;

const StockTag = styled.div`
  font-size: 10px; font-weight: 700; margin-top: 3px;
  color: ${p => p.warn ? p.theme.colors.danger : p.theme.colors.success};
  display: flex; align-items: center; gap: 3px;
`;

const formatPrice = p => Number(p || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

const emptyItem = () => ({ id: Date.now(), inventario_item_id: '', nombre: '', cantidad: 1, precio_unitario: 0, total: 0, notas: '', stock_disponible: null });
const computeTotal = (item) => parseFloat(item.precio_unitario || 0) * parseFloat(item.cantidad || 1);

const NuevoPedido = () => {
  const navigate = useNavigate();
  const { toast } = useModal();
  const { config } = useConfig();

  const [clientes, setClientes] = useState([]);
  const [inventarioItems, setInventarioItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    cliente_id: '', estado: 'pendiente',
    fecha_inicio: '', fecha_entrega: '', notas: '',
  });
  const [items, setItems] = useState([emptyItem()]);

  const isProyecto = config?.tipo_negocio === 'servicios';
  const tipoActual = isProyecto ? 'proyecto' : 'pedido';

  useEffect(() => {
    Promise.all([
      clientesAPI.getAll(),
      inventarioAPI.getAll(),
    ]).then(([cli, inv]) => {
      const tipo = config?.tipo_negocio === 'servicios' ? 'servicio' : 'producto';
      setClientes((cli.data.data || []).filter(c => c.estado === 'Activo' && !c.padre_cliente_id));
      setInventarioItems((inv.data.data || []).filter(i => i.tipo === tipo && i.activo));
    }).catch(() => {});
  }, [config?.tipo_negocio]); // eslint-disable-line

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleItemChange = (idx, field, value) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };

      if (field === 'inventario_item_id') {
        if (value) {
          const inv = inventarioItems.find(i => String(i.id) === String(value));
          if (inv) {
            next[idx].nombre = inv.nombre;
            next[idx].precio_unitario = Number(inv.precio) || 0;
            next[idx].stock_disponible = inv.tipo === 'producto' ? Number(inv.stock ?? 0) : null;
          }
        } else {
          next[idx].stock_disponible = null;
        }
      }
      next[idx].total = computeTotal(next[idx]);
      return next;
    });
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const total = items.reduce((s, i) => s + (i.total || 0), 0);

  const handleSubmit = async e => {
    e.preventDefault();
    if (items.some(i => !i.nombre.trim())) { setError('Todos los items necesitan un nombre'); return; }
    const sinStock = items.filter(i => i.stock_disponible !== null && Number(i.cantidad) > i.stock_disponible);
    if (sinStock.length > 0 && form.estado === 'completado') {
      setError(`Stock insuficiente: ${sinStock.map(i => i.nombre).join(', ')}. Reduce la cantidad o cambia el estado.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const clienteSel = clientes.find(c => String(c.id) === form.cliente_id);
      await pedidosAPI.create({
        tipo: tipoActual,
        cliente_id: form.cliente_id || null,
        cliente_nombre: clienteSel?.nombre || null,
        estado: form.estado,
        fecha_inicio: form.fecha_inicio || null,
        fecha_entrega: form.fecha_entrega || null,
        notas: form.notas || null,
        items: items.map(i => ({
          inventario_item_id: i.inventario_item_id || null,
          nombre: i.nombre.trim(),
          cantidad: Number(i.cantidad) || 1,
          precio_unitario: Number(i.precio_unitario) || 0,
          notas: i.notas || null,
        })),
      });
      toast(`${isProyecto ? 'Proyecto' : 'Pedido'} creado`, 'success');
      navigate('/pedidos');
    } catch (err) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate(-1)}><MdArrowBack /></BackButton>
        <Title>
          {isProyecto ? <MdWorkspaces /> : <MdShoppingCart />}
          {isProyecto ? 'Nuevo Proyecto' : 'Nuevo Pedido'}
        </Title>
      </PageHeader>

      <form onSubmit={handleSubmit}>
        {/* Info general */}
        <FormCard>
          <CardTitle>Informacion General</CardTitle>
          <Grid>
            <FieldGroup>
              <Label>Cliente</Label>
              <Select name="cliente_id" value={form.cliente_id} onChange={handleChange}>
                <option value="">Sin cliente</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </Select>
            </FieldGroup>
            <FieldGroup>
              <Label>Estado</Label>
              <Select name="estado" value={form.estado} onChange={handleChange}>
                <option value="pendiente">Pendiente</option>
                <option value="en_proceso">En proceso</option>
                <option value="completado">Completado</option>
                <option value="cancelado">Cancelado</option>
              </Select>
            </FieldGroup>
            <FieldGroup>
              <Label>{isProyecto ? 'Fecha de inicio' : 'Fecha del pedido'}</Label>
              <Input type="date" name="fecha_inicio" value={form.fecha_inicio} onChange={handleChange} />
            </FieldGroup>
            <FieldGroup>
              <Label>Fecha de entrega</Label>
              <Input type="date" name="fecha_entrega" value={form.fecha_entrega} onChange={handleChange} />
            </FieldGroup>
            <FieldGroup style={{ gridColumn: '1 / -1' }}>
              <Label>Notas</Label>
              <TextArea name="notas" value={form.notas} onChange={handleChange} placeholder="Observaciones..." />
            </FieldGroup>
          </Grid>
        </FormCard>

        {/* Items */}
        <FormCard>
          <CardTitle>{isProyecto ? 'Servicios incluidos' : 'Productos del pedido'}</CardTitle>

          {items.map((item, idx) => (
            <ItemRow key={item.id}>
              <ItemFieldGroup>
                <ItemLabel>{isProyecto ? 'Servicio' : 'Producto'} <span style={{ color: 'red' }}>*</span></ItemLabel>
                {inventarioItems.length > 0 && (
                  <ItemSelect
                    value={item.inventario_item_id}
                    onChange={e => handleItemChange(idx, 'inventario_item_id', e.target.value)}
                  >
                    <option value="">— Del catálogo —</option>
                    {inventarioItems.map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
                  </ItemSelect>
                )}
                <ItemInput
                  placeholder="Nombre del item"
                  value={item.nombre}
                  onChange={e => handleItemChange(idx, 'nombre', e.target.value)}
                />
                {!isProyecto && item.stock_disponible !== null && (
                  <StockTag warn={Number(item.cantidad) > item.stock_disponible}>
                    {Number(item.cantidad) > item.stock_disponible
                      ? <><MdWarning /> Sin stock suficiente ({item.stock_disponible} disponibles)</>
                      : `Stock: ${item.stock_disponible}`}
                  </StockTag>
                )}
              </ItemFieldGroup>

              <ItemFieldGroup>
                <ItemLabel>Cantidad</ItemLabel>
                <ItemInput
                  type="number" min="1" value={item.cantidad}
                  onChange={e => handleItemChange(idx, 'cantidad', e.target.value)}
                />
              </ItemFieldGroup>

              <ItemFieldGroup>
                <ItemLabel>Precio unit. ($)</ItemLabel>
                <ItemInput
                  type="number" min="0" step="0.01" value={item.precio_unitario}
                  onChange={e => handleItemChange(idx, 'precio_unitario', e.target.value)}
                />
              </ItemFieldGroup>

              <ItemFieldGroup>
                <ItemLabel>Total ($)</ItemLabel>
                <ItemInput
                  type="number"
                  value={(item.total || 0).toFixed(2)}
                  readOnly
                />
              </ItemFieldGroup>

              <DelBtn
                type="button"
                onClick={() => removeItem(idx)}
                disabled={items.length === 1}
                style={{ opacity: items.length === 1 ? 0.5 : 1, alignSelf: 'flex-end' }}
              >
                <MdDelete />
              </DelBtn>
            </ItemRow>
          ))}

          <AddItemBtn type="button" onClick={addItem}>
            <MdAdd /> Agregar {isProyecto ? 'servicio' : 'producto'}
          </AddItemBtn>
          <TotalRow>Total: {formatPrice(total)}</TotalRow>
        </FormCard>

        {error && <ErrMsg>{error}</ErrMsg>}

        <ButtonRow>
          <Btn type="button" onClick={() => navigate(-1)} disabled={submitting}>Cancelar</Btn>
          <Btn type="submit" primary disabled={submitting}>
            <MdSave /> {submitting ? 'Guardando...' : 'Crear'}
          </Btn>
        </ButtonRow>
      </form>
    </Container>
  );
};

export default NuevoPedido;
